import { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from '../../hooks/useTranslation';

/**
 * User type representing subscription status
 */
export type SubscriptionType = 'free' | 'premium';

/**
 * User role type
 */
export type UserRole = 'free' | 'premium' | 'admin';

/**
 * User interface for authenticated users
 */
export interface User {
  id?: string;
  email: string;
  role?: UserRole;
  subscriptionType: SubscriptionType;
}

/**
 * Props for the UserMenu component
 */
export interface UserMenuProps {
  /** Current authenticated user, or null if not logged in */
  user: User | null;
  /** Callback function to handle logout */
  onLogout?: () => void;
}

/**
 * UserMenu Component
 * 
 * Displays user authentication state and provides access to account features.
 * 
 * Features:
 * - Shows Login/Register buttons when logged out
 * - Shows dropdown menu when logged in (Profile, Subscription, Logout)
 * - Persistent "Upgrade to Premium" button for Free users
 * - Touch-friendly (44x44px minimum targets)
 * - Keyboard accessible with Escape to close
 * 
 * @validates Requirements 9.1, 10.1
 */
export function UserMenu({ user, onLogout }: UserMenuProps) {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const navigate = useNavigate();
  const { t } = useTranslation();

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        setIsDropdownOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Close dropdown on Escape key
  useEffect(() => {
    function handleEscape(event: KeyboardEvent) {
      if (event.key === 'Escape' && isDropdownOpen) {
        setIsDropdownOpen(false);
        buttonRef.current?.focus();
      }
    }

    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isDropdownOpen]);

  const toggleDropdown = () => {
    setIsDropdownOpen(!isDropdownOpen);
  };

  const handleLogout = () => {
    setIsDropdownOpen(false);
    onLogout?.();
    navigate('/');
  };

  const closeDropdown = () => {
    setIsDropdownOpen(false);
  };

  // Show upgrade button for Free users (logged in or not)
  const showUpgradeButton = !user || user.subscriptionType === 'free';

  // Logged out state
  if (!user) {
    return (
      <div className="flex items-center gap-2">
        {/* Upgrade button - always visible for non-premium users */}
        <Link
          to="/subscription"
          className="hidden sm:flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-accent-600 hover:text-accent-700 hover:bg-accent-50 rounded-lg transition-all duration-150 feedback"
        >
          <SparkleIcon className="w-4 h-4" />
          <span>{t('nav.premium')}</span>
        </Link>

        {/* Login link */}
        <Link
          to="/login"
          className="px-4 py-2 text-sm font-medium text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-slate-100 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-all duration-150 feedback min-h-touch min-w-touch flex items-center justify-center"
        >
          {t('nav.login')}
        </Link>

        {/* Register button */}
        <Link
          to="/register"
          className="btn-primary text-sm"
        >
          {t('nav.register')}
        </Link>
      </div>
    );
  }

  // Logged in state
  return (
    <div className="flex items-center gap-2">
      {/* Upgrade button - visible for Free users */}
      {showUpgradeButton && (
        <Link
          to="/subscription"
          className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-white bg-gradient-to-r from-accent-500 to-accent-600 hover:from-accent-600 hover:to-accent-700 rounded-lg shadow-sm hover:shadow transition-all duration-200 feedback"
        >
          <SparkleIcon className="w-4 h-4" />
          <span className="hidden sm:inline">{t('nav.upgradePremium')}</span>
          <span className="sm:hidden">{t('nav.premium')}</span>
        </Link>
      )}

      {/* User dropdown */}
      <div className="relative">
        <button
          ref={buttonRef}
          type="button"
          onClick={toggleDropdown}
          className="flex items-center gap-2 px-3 py-2 min-h-touch rounded-lg text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 transition-all duration-150 feedback"
          aria-expanded={isDropdownOpen}
          aria-haspopup="true"
          aria-label={t('userMenu.ariaLabel')}
        >
          {/* User avatar */}
          <div className="w-8 h-8 rounded-full bg-primary-100 dark:bg-primary-900/40 text-primary-600 dark:text-primary-400 flex items-center justify-center text-sm font-medium">
            {user.email.charAt(0).toUpperCase()}
          </div>
          
          {/* User info - hidden on mobile */}
          <div className="hidden md:flex flex-col items-start">
            <span className="text-sm font-medium text-slate-900 dark:text-slate-100 truncate max-w-[120px]">
              {user.email.split('@')[0]}
            </span>
            <span className="text-xs text-slate-500 dark:text-slate-400">
              {getSubscriptionLabel(user, t)}
            </span>
          </div>
          
          {/* Dropdown arrow */}
          <ChevronDownIcon 
            className={`w-4 h-4 text-slate-400 dark:text-slate-500 transition-transform duration-200 ${
              isDropdownOpen ? 'rotate-180' : ''
            }`}
          />
        </button>

        {/* Dropdown menu */}
        {isDropdownOpen && (
          <div
            ref={dropdownRef}
            className="absolute right-0 mt-2 w-56 bg-white dark:bg-slate-800 rounded-xl shadow-lg border border-slate-200 dark:border-slate-700 py-2 z-50 animate-scale-in origin-top-right"
            role="menu"
            aria-orientation="vertical"
          >
            {/* User info (visible on mobile only) */}
            <div className="md:hidden px-4 py-3 border-b border-slate-100 dark:border-slate-700">
              <p className="text-sm font-medium text-slate-900 dark:text-slate-100 truncate">
                {user.email}
              </p>
              <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                {getSubscriptionLabel(user, t)}
              </p>
            </div>

            {/* Menu items */}
            <div className="py-1">
              <MenuLink 
                to="/account" 
                icon={<UserIcon className="w-4 h-4" />}
                onClick={closeDropdown}
              >
                {t('userMenu.myProfile')}
              </MenuLink>
              
              <MenuLink 
                to="/subscription" 
                icon={<CreditCardIcon className="w-4 h-4" />}
                onClick={closeDropdown}
              >
                {t('userMenu.subscription')}
              </MenuLink>

              {/* Dictionaries link - only for premium users and admins */}
              {(user.subscriptionType === 'premium' || user.role === 'admin') && (
                <MenuLink 
                  to="/dictionaries" 
                  icon={<BookIcon className="w-4 h-4" />}
                  onClick={closeDropdown}
                >
                  {t('userMenu.dictionaries')}
                </MenuLink>
              )}

              {/* Admin link - only for admins */}
              {user.role === 'admin' && (
                <MenuLink 
                  to="/admin" 
                  icon={<ShieldIcon className="w-4 h-4" />}
                  onClick={closeDropdown}
                >
                  {t('userMenu.administration')}
                </MenuLink>
              )}
            </div>

            {/* Logout */}
            <div className="border-t border-slate-100 dark:border-slate-700 pt-1 mt-1">
              <button
                type="button"
                onClick={handleLogout}
                className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 hover:text-slate-900 dark:hover:text-slate-100 transition-colors duration-150"
                role="menuitem"
              >
                <LogoutIcon className="w-4 h-4" />
                {t('userMenu.logout')}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Menu link component for dropdown items
 */
interface MenuLinkProps {
  to: string;
  icon: React.ReactNode;
  onClick: () => void;
  children: React.ReactNode;
}

function MenuLink({ to, icon, onClick, children }: MenuLinkProps) {
  return (
    <Link
      to={to}
      onClick={onClick}
      className="flex items-center gap-3 px-4 py-2.5 text-sm text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 hover:text-slate-900 dark:hover:text-slate-100 transition-colors duration-150"
      role="menuitem"
    >
      {icon}
      {children}
    </Link>
  );
}

/**
 * Get subscription label for display
 */
function getSubscriptionLabel(user: User, t: (key: string) => string): string {
  if (user.role === 'admin') {
    return t('userMenu.admin');
  }
  switch (user.subscriptionType) {
    case 'premium':
      return t('userMenu.premium');
    case 'free':
    default:
      return t('userMenu.free');
  }
}

// Icon components
function SparkleIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.456 2.456L21.75 6l-1.035.259a3.375 3.375 0 00-2.456 2.456z" />
    </svg>
  );
}

function ChevronDownIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
    </svg>
  );
}

function UserIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z" />
    </svg>
  );
}

function CreditCardIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 8.25h19.5M2.25 9h19.5m-16.5 5.25h6m-6 2.25h3m-3.75 3h15a2.25 2.25 0 002.25-2.25V6.75A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25v10.5A2.25 2.25 0 004.5 19.5z" />
    </svg>
  );
}

function ShieldIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z" />
    </svg>
  );
}

function LogoutIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 9V5.25A2.25 2.25 0 0013.5 3h-6a2.25 2.25 0 00-2.25 2.25v13.5A2.25 2.25 0 007.5 21h6a2.25 2.25 0 002.25-2.25V15M12 9l-3 3m0 0l3 3m-3-3h12.75" />
    </svg>
  );
}

function BookIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 6.042A8.967 8.967 0 006 3.75c-1.052 0-2.062.18-3 .512v14.25A8.987 8.987 0 016 18c2.305 0 4.408.867 6 2.292m0-14.25a8.966 8.966 0 016-2.292c1.052 0 2.062.18 3 .512v14.25A8.987 8.987 0 0018 18a8.967 8.967 0 00-6 2.292m0-14.25v14.25" />
    </svg>
  );
}
